import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  MenuOutlined,
  SettingOutlined,
  DownOutlined,
} from '@ant-design/icons'
import { twMerge } from 'tailwind-merge'
import { useAuthorization } from '../contexts/AuthorizationContext'
import { useAuthSession } from '../lib/session'
import { useStaffDataEligibility } from '../hooks/useStaffDataEligibility'
import { buildNavSections, buildProfileMenu, resolveHomePath } from '../config/nav'
import { useAdvicePrepPaywall } from '@/features/adviceprep/hooks'
import { ADVICEPREP_PERMISSION_WITHHELD_ROUTE_KEYS } from '@/features/adviceprep/paywall/constants'
import { getNavSectionConfig } from '../config/navSections'
import { getFeatureTheme } from '../lib/productBranding'
import { permissions as permissionKeys, UserTypes } from '../types/authorization'

type SideNavProps = {
  collapsed: boolean
  onToggle: () => void
  /**
   * A nav item a walkthrough is pointing at, whose section must be expanded.
   * Tokenised because the same key can be requested twice.
   */
  forceOpenItem?: { key: string; token: number } | null
}

type RenderSection = ReturnType<typeof buildNavSections>[number]

function isSectionActive(section: RenderSection, pathname: string): boolean {
  return section.items.some((item) => item.path === pathname || (item.path !== '/clients' && pathname.startsWith(item.path + '/')))
}

function isItemActive(path: string, pathname: string): boolean {
  if (path === '/clients') return pathname === '/clients' || pathname.startsWith('/clients/')
  return pathname === path || pathname.startsWith(path + '/')
}

function filterStaffDataNavSections(sections: RenderSection[], canShowStaffData: boolean) {
  if (canShowStaffData) return sections

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.key !== 'client-staff'),
    }))
    .filter((section) => section.items.length > 0)
}

export default function SideNav({ collapsed, onToggle, forceOpenItem }: SideNavProps) {
  const pathname = useLocation().pathname
  const session = useAuthSession()
  const { permissions } = useAuthorization()
  const canViewStaffData = permissions.has(permissionKeys.viewStaffData)
  const staffDataEligibility = useStaffDataEligibility(canViewStaffData ? session?.currentClientId || '' : '')
  const advicePrepPaywall = useAdvicePrepPaywall()
  // A withheld permission and one the user never had look identical from here,
  // so forcing these back into the nav for everyone would show a ClientUser
  // admin areas they are not entitled to. Restrict it to the user types that
  // would otherwise hold them.
  const paywallHidesAdminAreas =
    !!advicePrepPaywall &&
    (session?.currentUserTypeId === UserTypes.ClientAdmin || session?.currentUserTypeId === UserTypes.Admin)
  const baseSections = useMemo(
    () =>
      buildNavSections(
        session,
        permissions,
        paywallHidesAdminAreas ? ADVICEPREP_PERMISSION_WITHHELD_ROUTE_KEYS : undefined
      ),
    [session, permissions, paywallHidesAdminAreas]
  )
  const sections = useMemo(
    () => filterStaffDataNavSections(baseSections, staffDataEligibility.isEligible),
    [baseSections, staffDataEligibility.isEligible]
  )
  const profileMenu = useMemo(() => buildProfileMenu(session, permissions), [session, permissions])
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const firstKey = sections[0]?.key
    if (firstKey && openKeys.size === 0) {
      setOpenKeys(new Set([firstKey]))
    }
  }, [sections, openKeys.size])

  // Open whichever section contains the page you are on. Landing somewhere via
  // a link, a pop-up or a walkthrough should show you where you are in the nav
  // rather than leaving it collapsed. Additive, so sections you opened by hand
  // stay open.
  useEffect(() => {
    const active = sections.find((section) => isSectionActive(section, pathname))
    if (!active) return
    setOpenKeys((prev) => (prev.has(active.key) ? prev : new Set(prev).add(active.key)))
  }, [pathname, sections])

  // A walkthrough pointing at a nav item that is not on the current page. The
  // effect above only opens the section you are in, so that link would sit in a
  // collapsed section with no box for the spotlight to find.
  const forcedKey = forceOpenItem?.key
  const forcedToken = forceOpenItem?.token
  useEffect(() => {
    if (!forcedKey) return
    const owner = sections.find((section) => section.items.some((item) => item.key === forcedKey))
    if (!owner) return
    setOpenKeys((prev) => (prev.has(owner.key) ? prev : new Set(prev).add(owner.key)))
  }, [forcedKey, forcedToken, sections])

  const toggleSection = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const defaultHome = resolveHomePath(session, permissions)

  const getSectionVars = (sectionKey: string): CSSProperties | undefined => {
    const themeKey = getNavSectionConfig(sectionKey)?.themeKey
    const theme = themeKey ? getFeatureTheme(themeKey) : null
    if (!theme) return undefined
    return {
      '--feature-nav-hover-bg': theme.soft,
      '--feature-nav-hover-text': theme.strong,
      '--feature-nav-active-bg': theme.navBackground,
      '--feature-nav-active-text': theme.navText,
      '--feature-nav-active-border': theme.navBorder,
    } as CSSProperties
  }

  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      <div className="h-14 lg:h-16 px-4 flex items-center justify-between gap-2 border-b border-gray-200">
        {!collapsed && (
          <Link to={defaultHome} className="flex items-center min-w-0">
            <img
              src="/assets/auth/logo-vividteal.svg"
              alt="Dashboard Insights"
              className="h-8 w-auto shrink-0"
            />
          </Link>
        )}
        <button type="button" onClick={onToggle} className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-500/30 transition-colors shrink-0 ml-auto" aria-label="Toggle menu">
          <MenuOutlined className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-2">
        {sections.map((section) => {
          const sectionActive = isSectionActive(section, pathname)
          const isOpen = openKeys.has(section.key)
          const themeKey = getNavSectionConfig(section.key)?.themeKey
          const sectionTheme = themeKey ? getFeatureTheme(themeKey) : null
          const sectionVars = getSectionVars(section.key)
          const sectionButtonStyle = sectionActive && sectionTheme
            ? {
                ...sectionVars,
                backgroundColor: sectionTheme.navBackground,
                color: sectionTheme.navText,
                borderLeftColor: sectionTheme.navBorder,
              } as CSSProperties
            : sectionVars
          return (
            <div key={section.key} className="mb-0.5">
              <button
                type="button"
                onClick={() => toggleSection(section.key)}
                style={sectionButtonStyle}
                className={twMerge(
                  'w-full flex items-center justify-between gap-2 pl-4 pr-4 py-2.5 text-left text-sm font-semibold font-display transition-colors border-l-4',
                  sectionActive
                    ? 'feature-nav-button border-l-4'
                    : sectionTheme
                      ? 'feature-nav-button border-transparent text-gray-700'
                      : 'border-transparent text-gray-700 hover:bg-gray-100'
                )}
              >
                <span>{section.label}</span>
                <DownOutlined className={twMerge('w-4 h-4 shrink-0 transition-transform', isOpen && 'rotate-180', sectionActive && 'text-white')} />
              </button>
              {isOpen && (
                <div className="bg-gray-50/80 border-l-4 border-transparent">
                  {section.items.map((item) => {
                    const active = isItemActive(item.path, pathname)
                    const Icon = item.Icon
                    const itemStyle = active && sectionTheme
                      ? {
                          ...sectionVars,
                          color: sectionTheme.strong,
                          backgroundColor: sectionTheme.soft,
                          borderLeftColor: sectionTheme.navBorder,
                        } as CSSProperties
                      : sectionVars
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        // Anchors the AdvicePrep walkthroughs, which start by
                        // pointing at where the product lives in the nav.
                        data-tour={`nav-${item.key}`}
                        style={itemStyle}
                        className={twMerge(
                          'feature-nav-link flex items-center gap-3 px-4 pl-8 py-2.5 text-sm font-medium transition-colors border-l-4 border-transparent',
                          active
                            ? 'feature-nav-link--active'
                            : sectionTheme
                              ? 'text-gray-700'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        )}
                      >
                        <span
                          className="feature-nav-icon w-4 h-4 shrink-0"
                          style={active && sectionTheme ? { color: sectionTheme.primary } : undefined}
                        >
                          <Icon className={twMerge('w-4 h-4', active ? '' : 'text-gray-500')} />
                        </span>
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>
      <div className="p-3 border-t border-gray-200 bg-gray-50/30">
        {profileMenu.some((item) => item.key === 'profile') && (
          <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-brand-50 hover:text-brand-700 transition-colors">
            <SettingOutlined className="w-5 h-5 text-gray-500" />
            <span className="font-display">My Settings</span>
          </Link>
        )}
        <div className="px-4 py-1.5 text-xs text-gray-500 font-medium">2026 © Dashboard Insights</div>
        <div className="px-4 text-xs text-gray-400">Version: 1.0.18</div>
        <div className="px-4 text-xs text-gray-400">API: 1.0.18</div>
      </div>
    </div>
  )
}
